/**
 * ChatSystem - AI Chat Assistant Component
 * 
 * A comprehensive chat system with SSE streaming, multiple conversations,
 * and modern UX design using Ant Design components.
 */

import React, { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import ChatDrawer from './ChatDrawer';
import ChatButton from './ChatButton';
import type { 
  ChatSystemConfig, 
  ChatSystemRef, 
  ChatEvents,
  ChatConversation 
} from './types';

export interface ChatSystemProps extends ChatSystemConfig, ChatEvents {
  /** Whether the chat is initially open */
  defaultOpen?: boolean;
  /** Custom button tooltip */
  buttonTooltip?: string;
  /** Whether to show badge on button */
  showBadge?: boolean;
  /** Badge configuration */
  badge?: {
    count?: number;
    dot?: boolean;
  };
  /** Custom styles */
  className?: string;
  style?: React.CSSProperties;
  /** Whether the chat system is disabled */
  disabled?: boolean;
}

/**
 * Main ChatSystem component that combines the floating button and drawer
 */
export const ChatSystem = forwardRef<ChatSystemRef, ChatSystemProps>(({
  // Config
  maxConversations = 5,
  typingSpeed = 2,
  typingInterval = 30,
  autoFocus = true,
  maxMessageLength = 4000,
  drawerWidth = 680,
  drawerPlacement = 'right',
  buttonPosition = { bottom: 24, right: 24 },
  buttonSize = 'large',
  
  // Props
  defaultOpen = false,
  buttonTooltip = "Chat with AI Assistant",
  showBadge = false,
  badge,
  className,
  style,
  disabled = false,
  
  // Events
  onConversationStart,
  onConversationEnd,
  onMessageSent,
  onMessageReceived,
  onError,
  onStreamingStart,
  onStreamingEnd,
}, ref) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleCloseChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpenChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    openChat: handleOpenChat,
    closeChat: handleCloseChat,
    sendMessage: (message: string) => {
      // This would need to be implemented with a ref to ChatDrawer
      console.log('Sending message via ref:', message);
    },
    createNewConversation: () => {
      // This would need to be implemented with a ref to ChatDrawer
      console.log('Creating new conversation via ref');
    },
    getActiveConversation: (): ChatConversation | undefined => {
      // This would need to be implemented with a ref to ChatDrawer
      return undefined;
    },
    getAllConversations: (): ChatConversation[] => {
      // This would need to be implemented with a ref to ChatDrawer
      return [];
    },
  }), [handleOpenChat, handleCloseChat]);

  return (
    <div className={className} style={style}>
      {/* Floating Chat Button */}
      <ChatButton
        onClick={handleToggleChat}
        chatOpen={isOpen}
        position={buttonPosition}
        size={buttonSize}
        disabled={disabled}
        badge={showBadge ? badge : undefined}
        tooltipTitle={buttonTooltip}
      />

      {/* Chat Drawer */}
      <ChatDrawer
        open={isOpen}
        onClose={handleCloseChat}
        width={drawerWidth}
        placement={drawerPlacement}
        maxConversations={maxConversations}
      />
    </div>
  );
});

ChatSystem.displayName = 'ChatSystem';

// Export all components and types
export { default as ChatDrawer } from './ChatDrawer';
export { default as ChatButton } from './ChatButton';
export { default as ChatMessage } from './components/ChatMessage';
export { default as ChatConversation } from './components/ChatConversation';
export { default as ChatInput } from './components/ChatInput';
export { default as ChatTabs } from './components/ChatTabs';

// Export utilities
export * from './utils/streamingUtils';

// Export types
export * from './types';

// Export services
export * from '@/services/chat';

export default ChatSystem;
